import { useCallback, useEffect, useState } from 'react';
import { applyTheme, getStoredTheme, type ThemeId } from '@/lib/theme';

/**
 * Tiny hook over the theme module. Reads the persisted theme on mount
 * and exposes a setter that both updates local state AND calls
 * `applyTheme` (which swaps body classes + writes localStorage). Keeping
 * the body-class apply inside the setter rather than an effect avoids a
 * paint where local state and body class disagree.
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState<ThemeId>(getStoredTheme);

  useEffect(() => {
    // Boot already called initTheme(), but on hot-reload the local
    // module state may be stale relative to body — re-sync.
    setThemeState(getStoredTheme());
  }, []);

  const setTheme = useCallback((next: ThemeId) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  return { theme, setTheme };
};
